import React from 'react';
import { renderSkuWithSoftBreaks } from '../../../utils/skuSoftBreak';
import { normalizeSku } from '../../../utils/normalizeSku';
import ProductImage from '../../shared/ProductImage';
import BundleWorkStatBox from '../BundleWorkStatBox';
import type { BundleWorkSourceScan } from '../../../utils/scanSessionCache';
import { isMissingPickShelf } from './bundleWorkOrderLogic';
import BundleWorkListHeading from './BundleWorkListHeading';
import '../OrdersList.css';
import '../SingleSkuOrdersList.css';
import '../BundleWorkOrdersList.css';

interface BundleWorkReturnViewProps {
  readonly parentSku: string;
  readonly parentName: string | null;
  readonly parentLabelId: string;
  readonly parentShelf: string;
  readonly sourceScans: readonly BundleWorkSourceScan[];
  readonly nameBySku: Readonly<Record<string, string>>;
  readonly onPrintLabels: () => void;
}

function shelfLabel(shelf: string | null | undefined): string {
  const value = shelf?.trim() ?? '';
  return value === '' ? 'N/A' : value;
}

function nameForSku(sku: string, nameBySku: Readonly<Record<string, string>>): string | null {
  return nameBySku[normalizeSku(sku)] ?? null;
}

function BundleWorkReturnChip({
  sku,
  itemName,
  labelId,
  shelf,
}: {
  readonly sku: string;
  readonly itemName: string | null;
  readonly labelId: string;
  readonly shelf: string;
}): React.ReactElement {
  return (
    <div className="order-box-container single-sku-row bwo-row bundle-work-return-chip">
      <div className="single-sku-thumb bwo-thumb">
        <ProductImage sku={sku} alt={itemName || sku} />
      </div>
      <div className="single-sku-row-main bwo-row-main">
        <div className="bwo-sku-block">
          <div className="single-sku-sku">{renderSkuWithSoftBreaks(sku)}</div>
          {itemName ? <div className="single-sku-name">{itemName}</div> : null}
        </div>
        <div className="bwo-metrics">
          <BundleWorkStatBox label="ID" value={labelId} />
          <BundleWorkStatBox label="Shelf" value={shelf} />
        </div>
      </div>
    </div>
  );
}

function BundleWorkReturnView({
  parentSku,
  parentName,
  parentLabelId,
  parentShelf,
  sourceScans,
  nameBySku,
  onPrintLabels,
}: BundleWorkReturnViewProps): React.ReactElement {
  const missingParentShelf = isMissingPickShelf(parentShelf);

  return (
    <div className="bundle-work-return bundle-work-page">
      <div className="bundle-work-return-list">
        <BundleWorkListHeading>
          Return materials to the warehouse
        </BundleWorkListHeading>
        {missingParentShelf ? (
          <div className="bundle-work-return-missing">
            <p className="bundle-work-return-plain">
              Item has no shelf, create pick location for {renderSkuWithSoftBreaks(parentSku)}
            </p>
            <button
              type="button"
              className="bundle-work-return-print-btn"
              onClick={onPrintLabels}
            >
              Print Warehouse Labels
            </button>
          </div>
        ) : (
          <BundleWorkReturnChip
            sku={parentSku}
            itemName={parentName}
            labelId={parentLabelId}
            shelf={parentShelf}
          />
        )}
        <hr className="bundle-work-return-divider" />
        {sourceScans.map((scan) => (
          <BundleWorkReturnChip
            key={scan.labelId}
            sku={scan.sku}
            itemName={nameForSku(scan.sku, nameBySku)}
            labelId={scan.labelId}
            shelf={shelfLabel(scan.shelfIndex)}
          />
        ))}
      </div>
    </div>
  );
}

export default BundleWorkReturnView;
